import React, { useMemo } from 'react';
import { buildHouseLuckChart, PalaceKey } from '../utils/houseLuckChart';

interface NinePalaceFlyingStarProps {
  houseYear: number;
  annualYear: number;
  sitting: PalaceKey;
  facing: PalaceKey;
  sittingAngle: number;
  facingAngle: number;
  planImageUrl?: string;
}

const gridBox = {
  x: 72,
  y: 142,
  width: 576,
  height: 474,
  centerX: 360,
  centerY: 379,
};

const rotatedRectSafeScale = (degrees: number, width: number, height: number) => {
  const radians = (Math.abs(degrees) % 180) * (Math.PI / 180);
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const rotatedWidth = width * cos + height * sin;
  const rotatedHeight = width * sin + height * cos;

  return Math.min(width / rotatedWidth, height / rotatedHeight, 1);
};

const NinePalaceFlyingStar: React.FC<NinePalaceFlyingStarProps> = ({
  houseYear,
  annualYear,
  sitting,
  facing,
  sittingAngle,
  facingAngle,
  planImageUrl,
}) => {
  const chart = useMemo(
    () => buildHouseLuckChart(houseYear, annualYear, sitting, facing, sittingAngle, facingAngle),
    [annualYear, facing, facingAngle, houseYear, sitting, sittingAngle],
  );
  const planRotation = chart.sittingAngle - 180;
  const planScale = rotatedRectSafeScale(planRotation, gridBox.width, gridBox.height);

  return (
    <div className="nine-palace-module">
      <div className="nine-palace-copy">
        <span className="section-kicker">Orientation model</span>
        <h3>{chart.periodText}运宅盘</h3>
        <p>
          以房屋年份与校准朝向建立参考模型，结合坐向、年度信息与空间方位生成朝向模型。
        </p>
      </div>

      <svg
        className="nine-palace-svg"
        viewBox="0 0 720 720"
        role="img"
        aria-labelledby="house-luck-title house-luck-desc"
      >
        <title id="house-luck-title">{chart.periodText}运宅盘</title>
        <desc id="house-luck-desc">
          房屋年份 {chart.houseYear} 属 {chart.periodText}运，组合运盘、坐山盘、向山盘与 {chart.annualYear} 年流年盘。
        </desc>
        <rect x="28" y="28" width="664" height="664" rx="8" className="nine-palace-background" />
        {planImageUrl && (
          <image
            href={planImageUrl}
            x={gridBox.x}
            y={gridBox.y}
            width={gridBox.width}
            height={gridBox.height}
            preserveAspectRatio="xMidYMid meet"
            className="nine-palace-plan-overlay"
            transform={`translate(${gridBox.centerX} ${gridBox.centerY}) rotate(${planRotation}) scale(${planScale}) translate(${-gridBox.centerX} ${-gridBox.centerY})`}
          />
        )}
        <rect x="28" y="28" width="664" height="664" rx="8" className="nine-palace-frame" />
        <text x="360" y="78" textAnchor="middle" className="nine-palace-heading">
          {chart.periodText}运宅盘
        </text>
        <text x="360" y="112" textAnchor="middle" className="nine-palace-subheading">
          坐{chart.sittingLabel}{chart.sittingMountainLabel}{Math.round(chart.sittingAngle)}° · 向{chart.facingLabel}{chart.facingMountainLabel}{Math.round(chart.facingAngle)}°
        </text>

        <g transform="translate(72 142)" className="nine-palace-grid-layer">
          {chart.cells.map((cell) => {
            const x = cell.x * 192;
            const y = cell.y * 158;
            const isCenter = cell.key === 'center';

            return (
              <g key={cell.key} transform={`translate(${x} ${y})`}>
                <rect
                  width="192"
                  height="158"
                  className={isCenter ? 'nine-palace-cell center' : 'nine-palace-cell'}
                />
                <text x="18" y="31" className="nine-palace-direction">
                  {cell.label} · {cell.trigram}{cell.palaceElement}
                </text>
                <text x="36" y="70" textAnchor="middle" className="nine-palace-corner-star mountain">
                  {cell.mountainStar}
                </text>
                <text x="156" y="70" textAnchor="middle" className="nine-palace-corner-star facing">
                  {cell.facingStar}
                </text>
                <text x="96" y="104" textAnchor="middle" className="nine-palace-star">
                  {cell.periodStar}
                </text>
                <text x="96" y="132" textAnchor="middle" className="nine-palace-annual-star">
                  年 {cell.annualStar}
                </text>
                <text x="18" y="142" className="nine-palace-layer-label">
                  山
                </text>
                <text x="174" y="142" textAnchor="end" className="nine-palace-layer-label">
                  向
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      <p className="nine-palace-disclaimer">
        户型叠图与朝向模型并非简单一一对应关系，图示仅用于辅助参看，具体方位仍以太极点、坐向与房间中心综合判断。
      </p>
    </div>
  );
};

export default NinePalaceFlyingStar;
